/**
 * Durable Main Command Clear Chat archives (#415).
 *
 * Archive boundaries are org-scoped and append-only. The current conversation
 * is everything after the latest boundary. History reopens earlier slices
 * read-only. Underlying owner_commands / plans / Observer records are never
 * deleted — only filtered for display.
 */
import { DEFAULT_OVERSEER_CONTEXT_LIMITS } from '@brunel/shared';
import type { CommandTurn } from './command-chat';
import type { SharedTimelineItem } from './shared-command-timeline';

/** One durable Clear Chat boundary persisted for an org. */
export interface MainCommandChatArchive {
  readonly id: string;
  readonly orgId: string;
  readonly archivedAt: string;
  readonly throughCommandSeq: number | null;
  readonly archivedBy?: string | null;
  readonly title?: string | null;
  readonly digest?: Record<string, unknown> | null;
  readonly lastActiveAt?: string | null;
  readonly continuedFromId?: string | null;
}

/** Row shape from brunel.main_command_chat_archives. */
export interface MainCommandChatArchiveRow {
  readonly org_id: string;
  readonly id: string;
  readonly archived_at: string;
  readonly through_command_seq: number | null;
  readonly archived_by?: string | null;
  readonly title?: string | null;
  readonly digest?: Record<string, unknown> | null;
  readonly last_active_at?: string | null;
  readonly continued_from_id?: string | null;
}

export function archiveFromRow(row: MainCommandChatArchiveRow): MainCommandChatArchive {
  return {
    id: row.id,
    orgId: row.org_id,
    archivedAt: row.archived_at,
    throughCommandSeq: row.through_command_seq,
    archivedBy: row.archived_by ?? null,
    title: row.title ?? null,
    digest: row.digest ?? null,
    lastActiveAt: row.last_active_at ?? null,
    continuedFromId: row.continued_from_id ?? null,
  };
}

/** Newest first. */
export function sortArchivesNewestFirst(
  archives: readonly MainCommandChatArchive[],
): MainCommandChatArchive[] {
  return [...archives].sort((a, b) => {
    const byTime = (Date.parse(b.archivedAt) || 0) - (Date.parse(a.archivedAt) || 0);
    if (byTime !== 0) return byTime;
    return b.id.localeCompare(a.id);
  });
}

export function latestArchive(
  archives: readonly MainCommandChatArchive[],
): MainCommandChatArchive | null {
  return sortArchivesNewestFirst(archives)[0] ?? null;
}

/** Previous (older) boundary relative to `archive` when newest-first. */
export function previousArchive(
  archives: readonly MainCommandChatArchive[],
  archive: MainCommandChatArchive,
): MainCommandChatArchive | null {
  const ordered = sortArchivesNewestFirst(archives);
  const index = ordered.findIndex((item) => item.id === archive.id);
  if (index < 0) return null;
  return ordered[index + 1] ?? null;
}

export function archiveLabel(archive: MainCommandChatArchive, nowMs: number = Date.now()): string {
  const titled = archive.title?.trim();
  if (titled) return titled;
  const at = Date.parse(archive.archivedAt);
  if (!Number.isFinite(at)) return 'Archived chat';
  const delta = Math.max(0, nowMs - at);
  if (delta < 60_000) return 'Archived · just now';
  if (delta < 3_600_000) {
    const mins = Math.max(1, Math.round(delta / 60_000));
    return `Archived · ${mins} min ago`;
  }
  if (delta < 86_400_000) {
    const hours = Math.max(1, Math.round(delta / 3_600_000));
    return `Archived · ${hours} hr ago`;
  }
  const days = Math.max(1, Math.round(delta / 86_400_000));
  return `Archived · ${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Command turns after an exclusive lower seq bound and optional inclusive upper.
 * Overseer replies stay attached to their owner-command group.
 */
export function commandTurnsInArchiveRange(
  turns: readonly CommandTurn[],
  afterSeq: number | null,
  throughSeq: number | null = null,
): readonly CommandTurn[] {
  const visible: CommandTurn[] = [];
  let showGroup = false;
  for (const turn of turns) {
    if (turn.mine && turn.seq !== null) {
      const afterOk = afterSeq === null || turn.seq > afterSeq;
      const throughOk = throughSeq === null || turn.seq <= throughSeq;
      showGroup = afterOk && throughOk;
    }
    if (showGroup) visible.push(turn);
  }
  return visible;
}

function observerInRange(
  occurredAtMs: number | null,
  afterAtMs: number | null,
  throughAtMs: number | null,
): boolean {
  if (occurredAtMs === null || !Number.isFinite(occurredAtMs)) return false;
  if (afterAtMs !== null && occurredAtMs <= afterAtMs) return false;
  if (throughAtMs !== null && occurredAtMs > throughAtMs) return false;
  return true;
}

/**
 * Shared timeline slice for current or archived conversation.
 * - Current: after = latest boundary, through = null
 * - Archived segment: after = previous boundary (or null), through = selected
 */
export function timelineInArchiveRange(
  items: readonly SharedTimelineItem[],
  after: MainCommandChatArchive | null,
  through: MainCommandChatArchive | null = null,
): readonly SharedTimelineItem[] {
  const afterSeq = after?.throughCommandSeq ?? null;
  const throughSeq = through?.throughCommandSeq ?? null;
  const afterAtMs = after ? Date.parse(after.archivedAt) : null;
  const throughAtMs = through ? Date.parse(through.archivedAt) : null;
  const afterAt = afterAtMs !== null && Number.isFinite(afterAtMs) ? afterAtMs : null;
  const throughAt = throughAtMs !== null && Number.isFinite(throughAtMs) ? throughAtMs : null;

  const visible: SharedTimelineItem[] = [];
  let showCommandGroup = false;
  for (const item of items) {
    if (item.authority === 'observer_discussion') {
      if (observerInRange(item.occurredAtMs, afterAt, throughAt)) visible.push(item);
      continue;
    }
    const turn = item.commandTurn;
    if (turn?.mine && turn.seq !== null) {
      const afterOk = afterSeq === null || turn.seq > afterSeq;
      const throughOk = throughSeq === null || turn.seq <= throughSeq;
      showCommandGroup = afterOk && throughOk;
    }
    if (showCommandGroup) visible.push(item);
  }
  return visible;
}

/** Seq to send when archiving the currently visible conversation. */
export function archiveThroughCommandSeq(
  visibleTurns: readonly CommandTurn[],
  previous: MainCommandChatArchive | null,
): number | null {
  let latest: number | null = null;
  for (const turn of visibleTurns) {
    if (turn.mine && turn.seq !== null && (latest === null || turn.seq > latest)) {
      latest = turn.seq;
    }
  }
  if (latest !== null) return latest;
  return previous?.throughCommandSeq ?? null;
}

/** True when Clear should be enabled (any visible shared content). */
export function canArchiveConversation(visibleTimeline: readonly SharedTimelineItem[]): boolean {
  return visibleTimeline.length > 0;
}

/** Load org archive boundaries newest first (RLS member read). */
export async function loadMainCommandChatArchives(
  client: Pick<import('@supabase/supabase-js').SupabaseClient, 'schema'>,
  orgId: string,
): Promise<MainCommandChatArchive[]> {
  const { data, error } = await client.schema('brunel')
    .from('main_command_chat_archives')
    .select('org_id,id,archived_at,through_command_seq,archived_by,title,digest,last_active_at,continued_from_id')
    .eq('org_id', orgId)
    .order('archived_at', { ascending: false })
    .limit(100);
  if (error || !Array.isArray(data)) return [];
  return sortArchivesNewestFirst((data as MainCommandChatArchiveRow[]).map(archiveFromRow));
}

/** Current conversation metadata (#450) — not a second message store. */
export interface MainCommandConversationState {
  readonly conversationId: string;
  readonly title: string | null;
  readonly createdAt: string;
  readonly lastActiveAt: string;
  readonly continuedFromArchiveId: string | null;
  readonly seedDigest: Record<string, unknown> | null;
  readonly overseerSessionEpoch: number;
  readonly contextStatus: 'fresh' | 'growing' | 'rotated';
  readonly recentTurnCount: number;
  /** #688: absent only while an older database response is still in use. */
  readonly contextBasis?: 'measured' | 'estimated' | 'unavailable';
  readonly contextTokens?: number;
  readonly contextCharacters?: number;
  readonly contextWords?: number;
  readonly contextEstimatedTokens?: number;
  readonly contextNativeTokens?: number | null;
  /**
   * Configured token budget when the conversation read includes one.
   * Absent or not a positive integer means the budget is unknown.
   */
  readonly contextBudgetTokens?: number | null;
  /** #688 latest durable automatic rollover lineage; absent on older databases. */
  readonly contextRollover?: MainCommandContextRollover | null;
}

export interface MainCommandContextRollover {
  readonly rolloverId: string;
  readonly fromEpoch: number;
  readonly toEpoch: number;
  readonly reason: string;
  readonly status: 'pending' | 'acknowledged' | 'failed';
  readonly failure: string | null;
  /** When the epoch was acknowledged. Required before a chat page can split. */
  readonly acknowledgedAt?: string | null;
}

export interface MainCommandConversationStateRow {
  readonly conversationId?: string;
  readonly conversation_id?: string;
  readonly title?: string | null;
  readonly createdAt?: string;
  readonly created_at?: string;
  readonly lastActiveAt?: string;
  readonly last_active_at?: string;
  readonly continuedFromArchiveId?: string | null;
  readonly continued_from_archive_id?: string | null;
  readonly seedDigest?: Record<string, unknown> | null;
  readonly seed_digest?: Record<string, unknown> | null;
  readonly overseerSessionEpoch?: number;
  readonly overseer_session_epoch?: number;
  readonly contextStatus?: 'fresh' | 'growing' | 'rotated';
  readonly context_status?: 'fresh' | 'growing' | 'rotated';
  readonly recentTurnCount?: number;
  readonly recent_turn_count?: number;
  readonly contextBasis?: 'measured' | 'estimated' | 'unavailable';
  readonly context_basis?: 'measured' | 'estimated' | 'unavailable';
  readonly contextTokens?: number;
  readonly context_tokens?: number;
  readonly contextCharacters?: number;
  readonly context_characters?: number;
  readonly contextWords?: number;
  readonly context_words?: number;
  readonly contextEstimatedTokens?: number;
  readonly context_estimated_tokens?: number;
  readonly contextNativeTokens?: number | null;
  readonly context_native_tokens?: number | null;
  readonly contextBudgetTokens?: number | null;
  readonly context_budget_tokens?: number | null;
  readonly contextRollover?: unknown;
  readonly context_rollover?: unknown;
}

function nonNegativeCount(...values: readonly unknown[]): number {
  const value = values.find((candidate) => typeof candidate === 'number'
    && Number.isSafeInteger(candidate) && candidate >= 0);
  return typeof value === 'number' ? value : 0;
}

function positiveTokenCount(...values: readonly unknown[]): number | null {
  const value = values.find((candidate) => typeof candidate === 'number'
    && Number.isSafeInteger(candidate) && candidate > 0);
  return typeof value === 'number' ? value : null;
}

function acknowledgedInstant(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Date(parsed).toISOString() : null;
}

function contextRolloverFromRpc(value: unknown): MainCommandContextRollover | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const rolloverId = typeof row['rolloverId'] === 'string' ? row['rolloverId'] : null;
  const fromEpoch = typeof row['fromEpoch'] === 'number' && Number.isSafeInteger(row['fromEpoch']) ? row['fromEpoch'] : null;
  const toEpoch = typeof row['toEpoch'] === 'number' && Number.isSafeInteger(row['toEpoch']) ? row['toEpoch'] : null;
  const reason = typeof row['reason'] === 'string' ? row['reason'] : null;
  const status = row['status'];
  const failure = typeof row['failure'] === 'string' ? row['failure'] : null;
  if (rolloverId === null || fromEpoch === null || fromEpoch <= 0 || toEpoch !== fromEpoch + 1
    || reason === null || (status !== 'pending' && status !== 'acknowledged' && status !== 'failed')) return null;
  return {
    rolloverId, fromEpoch, toEpoch, reason, status, failure,
    acknowledgedAt: acknowledgedInstant(row['acknowledgedAt'] ?? row['acknowledged_at']),
  };
}

export function conversationStateFromRpc(value: MainCommandConversationStateRow | null | undefined): MainCommandConversationState | null {
  if (value == null) return null;
  const conversationId = value.conversationId ?? value.conversation_id;
  const createdAt = value.createdAt ?? value.created_at;
  const lastActiveAt = value.lastActiveAt ?? value.last_active_at;
  if (!conversationId || !createdAt || !lastActiveAt) return null;
  return {
    conversationId,
    title: value.title ?? null,
    createdAt,
    lastActiveAt,
    continuedFromArchiveId: value.continuedFromArchiveId ?? value.continued_from_archive_id ?? null,
    seedDigest: value.seedDigest ?? value.seed_digest ?? null,
    overseerSessionEpoch: value.overseerSessionEpoch ?? value.overseer_session_epoch ?? 1,
    contextStatus: value.contextStatus ?? value.context_status ?? 'fresh',
    recentTurnCount: value.recentTurnCount ?? value.recent_turn_count ?? 0,
    contextBasis: value.contextBasis ?? value.context_basis ?? 'unavailable',
    contextTokens: nonNegativeCount(value.contextTokens, value.context_tokens),
    contextCharacters: nonNegativeCount(value.contextCharacters, value.context_characters),
    contextWords: nonNegativeCount(value.contextWords, value.context_words),
    contextEstimatedTokens: nonNegativeCount(value.contextEstimatedTokens, value.context_estimated_tokens),
    contextNativeTokens: value.contextNativeTokens ?? value.context_native_tokens ?? null,
    contextBudgetTokens: positiveTokenCount(value.contextBudgetTokens, value.context_budget_tokens),
    contextRollover: contextRolloverFromRpc(value.contextRollover ?? value.context_rollover),
  };
}

/**
 * Short chat meter in the unit automatic rollover actually checks. The Overseer
 * rotates the chat once its recent turn count reaches the shared policy's
 * `maxRecentTurns`, so the meter counts turns against that same limit.
 */
export function mainCommandContextMeterLabel(
  state: MainCommandConversationState,
  maxTurns: number = DEFAULT_OVERSEER_CONTEXT_LIMITS.maxRecentTurns,
): string {
  const turns = state.recentTurnCount;
  if (!Number.isSafeInteger(turns) || turns < 0) return 'Chat usage unavailable';
  return `Chat ${Math.min(turns, maxTurns)} of ${maxTurns} turns`;
}

export const ROLLOVER_CHAT_PREFIX = 'rollover:';
export const CONTINUED_FROM_PREVIOUS_CHAT = 'Continued from the previous chat';

export interface RolloverBoundaryTurn {
  readonly seq: number | null;
  readonly sentAt?: string | null;
  readonly mine?: boolean;
}

/**
 * One synthetic archive for an acknowledged rollover. The id is the rollover id,
 * so a refresh or a repeated notification rebuilds the same page. Messages stay
 * in the timeline and are only filtered by the boundary.
 */
export function withRolloverChatArchive(
  archives: readonly MainCommandChatArchive[],
  conversation: MainCommandConversationState | null,
  turns: readonly RolloverBoundaryTurn[],
  orgId: string | null,
): MainCommandChatArchive[] {
  const stored = archives.filter((archive) => !archive.id.startsWith(ROLLOVER_CHAT_PREFIX));
  const boundary = rolloverChatBoundary(conversation, turns);
  if (orgId === null || orgId === '' || boundary === null) return stored;
  const covered = stored.some((archive) => archiveCoversRollover(archive, boundary.throughSeq, boundary.acknowledgedAtMs));
  if (covered) return stored;
  const archivedAt = new Date(boundary.acknowledgedAtMs).toISOString();
  return [...stored, {
    id: `${ROLLOVER_CHAT_PREFIX}${boundary.rolloverId}`,
    orgId,
    archivedAt,
    throughCommandSeq: boundary.throughSeq,
    title: 'Previous chat',
    digest: { rolloverId: boundary.rolloverId },
    lastActiveAt: archivedAt,
  }];
}

function rolloverChatBoundary(
  conversation: MainCommandConversationState | null,
  turns: readonly RolloverBoundaryTurn[],
): { readonly rolloverId: string; readonly acknowledgedAtMs: number; readonly throughSeq: number } | null {
  const rollover = conversation?.contextRollover;
  if (rollover == null || rollover.status !== 'acknowledged') return null;
  const acknowledgedAtMs = rollover.acknowledgedAt == null ? Number.NaN : Date.parse(rollover.acknowledgedAt);
  if (!Number.isFinite(acknowledgedAtMs)) return null;
  let throughSeq: number | null = null;
  for (const turn of turns) {
    if (turn.mine === false || turn.seq === null || turn.sentAt == null || turn.sentAt.trim() === '') continue;
    const sentAtMs = Date.parse(turn.sentAt);
    if (!Number.isFinite(sentAtMs) || sentAtMs > acknowledgedAtMs) continue;
    if (throughSeq === null || turn.seq > throughSeq) throughSeq = turn.seq;
  }
  if (throughSeq === null) return null;
  return { rolloverId: rollover.rolloverId, acknowledgedAtMs, throughSeq };
}

function archiveCoversRollover(
  archive: MainCommandChatArchive,
  throughSeq: number,
  acknowledgedAtMs: number,
): boolean {
  if (archive.throughCommandSeq === null || archive.throughCommandSeq < throughSeq) return false;
  const archivedAtMs = Date.parse(archive.archivedAt);
  return Number.isFinite(archivedAtMs) && archivedAtMs >= acknowledgedAtMs;
}

/** The one continuation line, only on the current page opened by a rollover. */
export function continuedFromPreviousChat(
  viewingArchiveId: string | null,
  archives: readonly MainCommandChatArchive[],
): string | null {
  if (viewingArchiveId !== null) return null;
  const latest = latestArchive(archives);
  if (latest !== null && latest.id.startsWith(ROLLOVER_CHAT_PREFIX)) return CONTINUED_FROM_PREVIOUS_CHAT;
  return null;
}

/** Latest automatic rollover state, kept explicit instead of inferring success from an epoch. */
export function mainCommandContextRolloverLabel(state: MainCommandConversationState): string | null {
  const rollover = state.contextRollover;
  if (rollover == null) return null;
  if (rollover.status === 'acknowledged') return `Rollover acknowledged · epoch ${rollover.fromEpoch}→${rollover.toEpoch}`;
  if (rollover.status === 'pending') return `Rollover pending · epoch ${rollover.fromEpoch}→${rollover.toEpoch}`;
  return `Rollover failed · epoch ${rollover.fromEpoch}→${rollover.toEpoch}`;
}

export type RecentMainCommandChat =
  | { readonly kind: 'current'; readonly id: string; readonly title: string; readonly createdAt: string; readonly lastActiveAt: string; readonly readOnly: false; readonly contextStatus: MainCommandConversationState['contextStatus']; readonly continuedFromId: string | null }
  | { readonly kind: 'archived'; readonly id: string; readonly title: string; readonly createdAt: string; readonly lastActiveAt: string; readonly archivedAt: string; readonly readOnly: true; readonly throughCommandSeq: number | null; readonly continuedFromId: string | null };

/** Current + archived chats, newest activity first (#450). */
export function listRecentMainCommandChats(input: {
  readonly current: MainCommandConversationState | null;
  readonly archives: readonly MainCommandChatArchive[];
  readonly currentFallbackTitle?: string;
}): RecentMainCommandChat[] {
  const items: RecentMainCommandChat[] = [];
  if (input.current) {
    items.push({
      kind: 'current',
      id: input.current.conversationId,
      title: input.current.title?.trim() || input.currentFallbackTitle || 'Current chat',
      createdAt: input.current.createdAt,
      lastActiveAt: input.current.lastActiveAt,
      readOnly: false,
      contextStatus: input.current.contextStatus,
      continuedFromId: input.current.continuedFromArchiveId,
    });
  }
  for (const archive of sortArchivesNewestFirst(input.archives)) {
    items.push({
      kind: 'archived',
      id: archive.id,
      title: archiveLabel(archive),
      createdAt: archive.archivedAt,
      lastActiveAt: archive.lastActiveAt ?? archive.archivedAt,
      archivedAt: archive.archivedAt,
      readOnly: true,
      throughCommandSeq: archive.throughCommandSeq,
      continuedFromId: archive.continuedFromId ?? null,
    });
  }
  return items;
}

export async function loadMainCommandConversationState(
  client: Pick<import('@supabase/supabase-js').SupabaseClient, 'rpc'>,
  orgId: string,
): Promise<MainCommandConversationState | null> {
  const { data, error } = await client.rpc('ensure_main_command_conversation_state', {
    request: { orgId },
  });
  if (error) return null;
  const result = data as { ok?: boolean; value?: MainCommandConversationStateRow | null } | null;
  if (!result?.ok) return null;
  return conversationStateFromRpc(result.value ?? null);
}
